/**
 * Hook para gerenciamento de objetos do pedido
 */

import { useState, useCallback, useEffect } from 'react';
import { OrderObject, ProductRules } from '../types/objects';
import { validateObjectDimensions } from '../utils/validators';
import { sortByExportIndex } from '../utils/sortByExportIndex';
import {
  focarObjeto,
  atualizarMultiplicadorUnicoPorStaticID,
  extrairNomeBaseObjeto
} from '../../../lib/corelOperations';
// import { corelCommunication } from '../../../lib/corel/communication';
import { debugLog, sendToTerminal } from '../../../lib/terminalLogger';
import { mmToCm } from '../../../lib/units';
import { usePermissions } from '../../../contexts/PermissionContext';

interface UseObjectManagementProps {
  initialContraction: string;
  productRules?: ProductRules;
  selectedPedido?: any;
}

export const useObjectManagement = ({ 
  initialContraction, 
  productRules, 
  selectedPedido 
}: UseObjectManagementProps) => {
  const [selectedObjects, setSelectedObjects] = useState<OrderObject[]>([]);
  const { role } = usePermissions();

  // Carregar objetos iniciais
  useEffect(() => {
    const carregarObjetosIniciais = async () => {
      try {
        // Coleta desativada neste fluxo – snapshot + monitor preenchem os dados
        // ✅ Log removido - reduzir verbosidade
      } catch (error) {
        // debug reduzido
      }
    };

    carregarObjetosIniciais();
  }, [initialContraction, productRules, role, selectedPedido]);

  // Atualizar quantidade de um objeto (aplicar somente após confirmação do C#)
  const updateObjectQuantity = useCallback(async (id: string, newQuantity: number) => {
    // Buscar o objeto primeiro, fora do setState
    const objectToUpdate = selectedObjects.find(obj => obj.id === id);
    
    if (!objectToUpdate) return;
    
    const updatedQuantity = Math.max(1, newQuantity);
    const usaContracaoVariavel = objectToUpdate.contraction !== undefined;
    const nomeBase = extrairNomeBaseObjeto(objectToUpdate.nome);
    const objectContraction = objectToUpdate.contraction || '0px';

    // Não aplicar no estado ainda; aguardar confirmação do C# via callback
    await atualizarMultiplicadorUnicoPorStaticID(
      objectToUpdate.staticId, 
      updatedQuantity,
      nomeBase,
      (_objetosAtualizados: any[], meta?: { status?: string; itens?: any[] }) => {
        // Confirmar somente quando status === 'ok' e houver item correspondente
        const statusOk = meta?.status === 'ok';
        const itens = Array.isArray(meta?.itens) ? meta!.itens as any[] : undefined;
        const item = itens?.find((x: any) => String(x?.staticId ?? x?.StaticID) === String(objectToUpdate.staticId));
        const sucesso = item ? item.sucesso !== false : statusOk;

        setSelectedObjects(prev => {
          return prev.map(obj => {
            if (obj.id !== id) return obj;
            if (!sucesso) {
              // Rejeitado: manter valor anterior (sem alterações)
              return obj;
            }
            // Aprovado: aplicar quantidade confirmada
            const multiplicadorConfirmado = typeof item?.multiplicador === 'number' ? item.multiplicador : updatedQuantity;
            const contracaoConfirmada = item?.contracao != null ? String(item.contracao) : obj.contraction;
            return {
              ...obj,
              quantity: Math.max(1, multiplicadorConfirmado),
              contraction: contracaoConfirmada != null && contracaoConfirmada !== '0' ? String(contracaoConfirmada) : (obj.contraction || '0px')
            };
          });
        });
      },
      usaContracaoVariavel,
      objectContraction,
      (objectToUpdate as any).guid
    );
  }, [selectedObjects]);

  // Focar um objeto no Corel usando StaticID
  const handleView = useCallback((staticId: string) => {
    debugLog('CSHARP_BRIDGE:SEND', { method: 'focar-por-staticid', payloadSize: 0, refId: staticId });
    
    // Verificar se é um ID ou StaticID
    if (staticId && staticId.startsWith('obj-')) {
      // Se for um ID, procuramos o objeto correspondente
      const objeto = selectedObjects.find(obj => obj.id === staticId);
      if (objeto) {
        debugLog('CSHARP_BRIDGE:SEND', { method: 'focar-por-id-convertido', payloadSize: 0, refId: objeto.staticId });
        staticId = objeto.staticId;
      } else {
        debugLog('ERROR', { scope: 'focar-objeto-por-staticid', errorMessage: `Objeto não encontrado: ${staticId}` });
        alert(`Erro: Não foi possível encontrar o objeto "${staticId}"`);
        return;
      }
    }
    
    // Verificar se o StaticID é válido
    if (!staticId || staticId.trim() === '') {
      debugLog('ERROR', { scope: 'focar-objeto-por-staticid', errorMessage: 'StaticID inválido' });
      return;
    }
    
    // Enviar o comando para o Corel com um callback para tratar a resposta
    const startedAt = Date.now();
    const requestId = focarObjeto(staticId, (response) => {
      const durationMs = Date.now() - startedAt;
      if (response.status === 'ok') {
        debugLog('CSHARP_BRIDGE:RECV', { event: 'focar-por-staticid', status: 'ok', refId: staticId, durationMs });
      } else {
        debugLog('CSHARP_BRIDGE:ERROR', { event: 'focar-por-staticid', errorMessage: response.message || 'Erro desconhecido', refId: staticId, durationMs });
        alert(`Erro ao focar objeto: ${response.message || 'Erro desconhecido'}`);
      }
    });
    
    if (requestId) {
      debugLog('CSHARP_BRIDGE:SEND', { method: 'comando-enviado', refId: requestId, payloadSize: 0 });
    } else {
      debugLog('CSHARP_BRIDGE:ERROR', { event: 'focar-objeto-por-staticid', errorMessage: 'WebView não disponível' });
      alert('Não foi possível conectar ao CorelDraw. Verifique se o aplicativo está aberto.');
    }
  }, [selectedObjects]);

  // Função para atualizar os objetos do Corel
  const refreshObjects = useCallback(async () => {
    return selectedObjects;
  }, [selectedObjects]);

  // Nova função para sincronização total (incluindo nomes)
  const syncAllWithCorel = useCallback(async () => {
    return selectedObjects;
  }, [selectedObjects]);

  // Revalidar objetos quando regras ou lista de objetos mudarem (ex.: após snapshot)
  useEffect(() => {
    if (selectedObjects.length === 0 || !productRules) return;

    const validatedObjects = validateObjectDimensions(selectedObjects, productRules);
    const needsUpdate = validatedObjects.some((obj, index) => {
      const current = selectedObjects[index];
      return !current || obj.status !== current.status || obj.message !== current.message;
    });

    if (needsUpdate) {
      setSelectedObjects(validatedObjects);
    }
  }, [productRules, selectedObjects, setSelectedObjects]);

  return {
    selectedObjects,
    setSelectedObjects,
    updateObjectQuantity,
    handleView,
    refreshObjects,
    syncAllWithCorel,
    /**
     * Aplica atualização/criação recebida do Corel (evento unificado categoria=objeto acao=atualizar)
     * Espera dados no formato { objeto: { staticId, nome, largura(mm), altura(mm), camada, pageIndex, zOrder, bbox, meta } }
     */
    applyCorelObjetoAtualizar: (dados: any) => {
      try {
        const o = (dados && (dados.objeto || dados)) || {};
        const staticId: string | undefined = String(o.staticId || dados?.staticId || '').trim();
        if (!staticId) return;
        
        // 🔍 DEBUG: Log valores RAW do C# ANTES da conversão
        sendToTerminal(`🔍 [APPLY_COREL_ATUALIZAR] RAW: ${o.nome} | largura=${o.largura} altura=${o.altura}`, {
          staticId,
          nome: o.nome,
          larguraRaw: o.largura,
          alturaRaw: o.altura,
        });
        
        // ✅ CORREÇÃO BUG: Backend C# retorna POLEGADAS nos eventos de atualização
        // mas MILÍMETROS no comando sync. Detectamos automaticamente:
        // - Valores típicos em mm: 500-5000 (objetos de 50-500cm)
        // - Valores típicos em inches: 20-200 (mesmos objetos)
        // Se ambos valores forem < 300, provavelmente estão em polegadas
        let larguraMm = o.largura;
        let alturaMm = o.altura;
        let unidadeDetectada: 'mm' | 'inches' = 'mm';
        
        if (typeof o.largura === 'number' && typeof o.altura === 'number') {
          // Heurística: valores em polegadas são tipicamente < 300 para objetos comuns
          // Valores em mm seriam > 300 para o mesmo objeto
          if (o.largura < 300 && o.altura < 300) {
            // Provavelmente em polegadas - converter para mm
            larguraMm = o.largura * 25.4;
            alturaMm = o.altura * 25.4;
            unidadeDetectada = 'inches';
            
            sendToTerminal(`⚠️ [APPLY_COREL_ATUALIZAR] DETECTADO INCHES! ${o.nome} | ${o.largura}in → ${larguraMm.toFixed(1)}mm | ${o.altura}in → ${alturaMm.toFixed(1)}mm`, {
              staticId,
              originalUnit: 'inches',
              larguraInches: o.largura,
              alturaInches: o.altura,
              larguraMm,
              alturaMm
            });
          }
        }
        
        // Conversão mm → cm
        let widthCm: number | undefined = undefined;
        let heightCm: number | undefined = undefined;
        if (typeof larguraMm === 'number' && typeof alturaMm === 'number') {
          widthCm = mmToCm(larguraMm);
          heightCm = mmToCm(alturaMm);
          
          sendToTerminal(`✅ [APPLY_COREL_ATUALIZAR] FINAL: ${o.nome} | ${widthCm?.toFixed(2)}cm x ${heightCm?.toFixed(2)}cm | unidade=${unidadeDetectada}`, {
            staticId,
            widthCm,
            heightCm,
            unidadeDetectada
          });
        }
        const nome = o.nome != null ? String(o.nome) : undefined;
        const guid = o?.meta?.guid ?? o.guid ?? undefined;
        const multiplicadorRaw = o?.meta?.multiplicador ?? o.multiplicador;
        const multiplicador = (multiplicadorRaw != null && !Number.isNaN(Number(multiplicadorRaw))) ? Number(multiplicadorRaw) : undefined;
        const contracao = o?.meta?.contracao ?? o.contracao;
        const pageIndex = (o?.pageIndex != null ? Number(o.pageIndex) : (o?.meta?.pageIndex != null ? Number(o.meta.pageIndex) : undefined));
        const exportIndexRaw = o?.meta?.exportIndex ?? o.exportIndex;
        const exportIndex = (exportIndexRaw == null || Number.isNaN(Number(exportIndexRaw))) ? null : Number(exportIndexRaw);

        setSelectedObjects(prev => {
          const idx = prev.findIndex(p => p.staticId === staticId);
          if (idx >= 0) {
            const atual = prev[idx];
            const atualizado: OrderObject = {
              ...atual,
              nome: nome ?? atual.nome,
              width: typeof widthCm === 'number' ? parseFloat(widthCm.toFixed(2)) : atual.width,
              height: typeof heightCm === 'number' ? parseFloat(heightCm.toFixed(2)) : atual.height,
              guid: guid ?? atual.guid,
              quantity: typeof multiplicador === 'number' && multiplicador > 0 ? multiplicador : atual.quantity,
              contraction: contracao != null ? (String(contracao) === '0' ? '0px' : String(contracao)) : atual.contraction,
              pageIndex: typeof pageIndex === 'number' ? pageIndex : atual.pageIndex,
              exportIndex: exportIndex !== null ? exportIndex : (atual.exportIndex ?? null)
            };
            const base = [...prev];
            base[idx] = validateObjectDimensions([atualizado], productRules)[0];
            // Reordenar imediatamente pela nova prioridade de exportação
            return sortByExportIndex(base as any, { nulls: 'last' });
          }
          // Novo objeto
          const novo: OrderObject = validateObjectDimensions([
            {
              id: `obj-${staticId}`,
              nome: nome || `OBJ_${staticId}`,
              staticId,
              guid: guid ?? undefined,
              width: parseFloat((widthCm ?? 0).toFixed(2)),
              height: parseFloat((heightCm ?? 0).toFixed(2)),
              quantity: typeof multiplicador === 'number' && multiplicador > 0 ? multiplicador : 1,
              totalArea: undefined as any, // será setado pelo validator
              status: 'success',
              contraction: contracao == null || String(contracao) === '0' ? (initialContraction || '0px') : String(contracao),
              include: true,
              pageIndex: typeof pageIndex === 'number' ? pageIndex : undefined,
              exportIndex: exportIndex
            }
          ], productRules)[0];
          const next = [...prev, novo];
          return sortByExportIndex(next as any, { nulls: 'last' });
        });
      } catch {}
    },
    /**
     * Remove objeto do grid por StaticID (evento unificado categoria=objeto acao=remover)
     */
    applyCorelObjetoRemover: (staticId: string) => {
      try {
        if (!staticId) return;
        setSelectedObjects(prev => prev.filter(p => p.staticId !== staticId));
      } catch {}
    }
  };
}; 